import { Helmet } from "react-helmet-async";
import { Link, Navigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { getPressRelease, formatPressDate, pressReleaseSeo } from "@/content/press";
import CanonicalTags from "@/components/seo/CanonicalTags";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import { canonicalUrl, SITE_ORIGIN } from "@/lib/seo";

/**
 * Release detail page (/press/:slug) — the reusable article template.
 *
 * Renders any published release from the markdown loader (src/content/press).
 * Unknown or draft slugs redirect back to the newsroom. The markdown body is
 * rendered with react-markdown and styled by `.mk-article`; per-release SEO
 * (title, description, Open Graph, NewsArticle JSON-LD) comes from frontmatter,
 * with optional `seoTitle` / `seoDescription` overriding only the search copy.
 */
const PressReleasePage = () => {
  const { slug } = useParams<{ slug: string }>();
  const release = getPressRelease(slug);

  // Unknown or draft slug → back to the newsroom.
  if (!release) return <Navigate to="/press" replace />;

  const releaseUrl = canonicalUrl(`/press/${release.slug}`);
  // Search copy may be overridden per release; the headline, standfirst and
  // JSON-LD below always describe the release as published.
  const { title: pageTitle, description: pageDescription } = pressReleaseSeo(release);
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    headline: release.title,
    datePublished: release.date,
    description: release.summary,
    author: { "@type": "Organization", name: "Compaire" },
    publisher: {
      "@type": "Organization",
      name: "Compaire",
      logo: { "@type": "ImageObject", url: `${SITE_ORIGIN}/compaire-logo.png` },
    },
    mainEntityOfPage: releaseUrl,
  };
  const dateline = [release.city, formatPressDate(release.date)].filter(Boolean).join(" — ");

  return (
    <MarketingLayout>
      <CanonicalTags path={`/press/${release.slug}`} title={pageTitle} description={pageDescription} />
      <Helmet>
        <meta property="og:type" content="article" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <article>
        <header
          style={{
            padding: "clamp(48px,7cqi,96px) 0 clamp(48px,6cqi,72px)",
            borderBottom: "1px solid var(--mk-border)",
          }}
        >
          <div className="mk-container mk-stack" style={{ gap: 28 }}>
            <Link to="/press" className="mk-backlink">
              ← Newsroom
            </Link>
            {release.category && <span className="mk-label">{release.category}</span>}
            <h1 className="mk-h1" style={{ maxWidth: 1040, font: "700 var(--mk-text-cta-lg)/1.04 var(--mk-sans)" }}>
              {release.title}
            </h1>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "16px clamp(40px,6cqi,96px)",
                alignItems: "baseline",
                maxWidth: 1040,
              }}
            >
              {dateline && <span className="mk-sample">{dateline}</span>}
              {release.summary && (
                <p
                  style={{
                    flex: "1 1 420px",
                    font: "400 var(--mk-text-lede)/1.55 var(--mk-sans)",
                    color: "var(--mk-ink-2)",
                    textWrap: "pretty",
                  }}
                >
                  {release.summary}
                </p>
              )}
            </div>
          </div>
        </header>
        <div
          style={{
            background: "#fff",
            padding: "clamp(56px,7cqi,96px) var(--mk-gutter) clamp(80px,10cqi,128px)",
          }}
        >
          <div className="mk-article">
            {release.body && <ReactMarkdown>{release.body}</ReactMarkdown>}
            {release.externalUrl && (
              <a href={release.externalUrl} target="_blank" rel="noopener noreferrer">
                Read the original announcement ↗
              </a>
            )}
          </div>
        </div>
      </article>
    </MarketingLayout>
  );
};

export default PressReleasePage;
